import { useEffect, useMemo, useState, useCallback } from 'react';
import { RefreshCw, Filter } from 'lucide-react';
import { fetchFleetGrid, retryDay, pullNow } from './api';
import { type GridCell, type CellBucket, BUCKET_CLASSES, BUCKET_LABEL } from './types';

const POLL_MS = 60_000;

interface Selected {
  readonly cell: GridCell;
}

export function CompletenessGridTab() {
  const [cells, setCells] = useState<GridCell[]>([]);
  const [days, setDays] = useState(14);
  const [exceptionsOnly, setExceptionsOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Selected | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetchFleetGrid({ days, exceptionsOnly });
      setCells(res.cells);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load failed');
    }
  }, [days, exceptionsOnly]);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  // Pivot cells into store -> platform -> date map and the axis lists.
  const { storeIds, platforms, dateList, byKey } = useMemo(() => {
    const storeSet = new Set<number>();
    const platformSet = new Set<string>();
    const dateSet = new Set<string>();
    const map = new Map<string, GridCell>();
    for (const c of cells) {
      storeSet.add(c.storeId);
      platformSet.add(c.platform);
      dateSet.add(c.date);
      map.set(`${c.storeId}::${c.platform}::${c.date}`, c);
    }
    return {
      storeIds: [...storeSet].sort((a, b) => a - b),
      platforms: [...platformSet].sort((a, b) => a.localeCompare(b)),
      dateList: [...dateSet].sort(),
      byKey: map,
    };
  }, [cells]);

  // Worst bucket for a (store, platform) row across the date window, for the cell summary dot.
  const worstFor = useCallback(
    (storeId: number, platform: string): CellBucket | null => {
      const order: CellBucket[] = ['problem', 'retry', 'in_flight', 'good'];
      let worst: CellBucket | null = null;
      for (const d of dateList) {
        const c = byKey.get(`${storeId}::${platform}::${d}`);
        if (!c) continue;
        if (worst === null || order.indexOf(c.bucket) < order.indexOf(worst)) worst = c.bucket;
      }
      return worst;
    },
    [byKey, dateList],
  );

  const doAction = useCallback(
    async (kind: 'retry' | 'pull', cell: GridCell) => {
      if (
        !confirm(
          `${kind === 'retry' ? 'Retry' : 'Pull now'} ${cell.platform} for store ${cell.storeId}?`,
        )
      ) {
        return;
      }
      try {
        if (kind === 'retry') await retryDay(cell.storeId, cell.platform, cell.date);
        else await pullNow(cell.storeId, cell.platform);
        showToast(`${kind} triggered for store ${cell.storeId} (${cell.platform})`);
        setSelected(null);
        void refresh();
      } catch (err) {
        showToast(err instanceof Error ? err.message : `${kind} failed`);
      }
    },
    [refresh],
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
          Window:
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="px-2 py-1 text-xs rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={31}>31 days</option>
          </select>
        </label>
        <button
          onClick={() => setExceptionsOnly((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border ${
            exceptionsOnly
              ? 'border-red-300 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300'
              : 'border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200'
          }`}
        >
          <Filter className="w-3 h-3" />
          {exceptionsOnly ? 'Showing problems only' : 'Show only problems'}
        </button>
        <button
          onClick={() => void refresh()}
          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {error ? (
        <div className="text-xs text-red-600 bg-red-50 dark:bg-red-950/40 rounded-lg px-3 py-2 mb-3">
          {error}
        </div>
      ) : null}

      {storeIds.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-10 text-center text-sm text-gray-400">
          {exceptionsOnly
            ? 'No problems in the selected window.'
            : 'No ledger data in the selected window.'}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-950 text-[10px] uppercase tracking-wide text-gray-500">
                  <th className="text-left px-3 py-2 font-medium sticky left-0 bg-gray-50 dark:bg-gray-950 min-w-[120px]">
                    Store
                  </th>
                  {platforms.map((p) => (
                    <th key={p} className="text-left px-3 py-2 font-medium whitespace-nowrap">
                      {p}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {storeIds.map((storeId) => (
                  <tr key={storeId} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="px-3 py-2 sticky left-0 bg-white dark:bg-gray-900 font-mono text-[11px] text-gray-700 dark:text-gray-300">
                      #{storeId}
                    </td>
                    {platforms.map((platform) => {
                      const worst = worstFor(storeId, platform);
                      const latest = [...dateList]
                        .reverse()
                        .map((d) => byKey.get(`${storeId}::${platform}::${d}`))
                        .find((c): c is GridCell => Boolean(c));
                      if (!worst || !latest) {
                        return (
                          <td key={platform} className="px-3 py-2 text-gray-300">
                            —
                          </td>
                        );
                      }
                      return (
                        <td key={platform} className="px-3 py-2">
                          <button
                            onClick={() => setSelected({ cell: latest })}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${BUCKET_CLASSES[worst]}`}
                            title={`${BUCKET_LABEL[worst]} — latest ${latest.date} (${latest.status})`}
                          >
                            {BUCKET_LABEL[worst]}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected ? (
        <div className="fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)} />
          <div className="ml-auto relative w-full max-w-md bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 p-4 h-full overflow-y-auto">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              store {selected.cell.storeId} / {selected.cell.platform}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              latest {selected.cell.date} · status {selected.cell.status} · {selected.cell.rowCount}{' '}
              rows
            </div>
            {selected.cell.severity ? (
              <div className="text-[11px] mt-1 text-amber-600">
                drift severity: {selected.cell.severity}
              </div>
            ) : null}
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => void doAction('retry', selected.cell)}
                className="px-3 py-1.5 text-xs rounded-md bg-amber-600 hover:bg-amber-700 text-white"
              >
                Retry this day
              </button>
              <button
                onClick={() => void doAction('pull', selected.cell)}
                className="px-3 py-1.5 text-xs rounded-md bg-blue-600 hover:bg-blue-700 text-white"
              >
                Pull now
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div className="fixed bottom-4 right-4 z-50 px-4 py-2.5 rounded-lg shadow-lg text-xs font-medium bg-gray-900 text-white">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
